// Build a CSV of the execution board from the same column array the table draws.
//
// Header text is each column's label. Cell text is the accessor, not the rendered JSX — a
// React node is not a spreadsheet value, and the accessor is what keeps a new column from
// needing a second export path. Quoting follows RFC 4180.

export interface CsvColumnLike<Row> {
  label: string;
  accessor: (row: Row) => string;
}

export function escapeCsvField(value: string): string {
  const needsQuotes = value.includes(',')
    || value.includes('"')
    || value.includes('\n')
    || value.includes('\r');
  if (!needsQuotes) return value;
  return '"' + value.replaceAll('"', '""') + '"';
}

export function buildCsv<Row>(rows: Row[], columns: CsvColumnLike<Row>[]): string {
  const lines: string[] = [];

  // One header cell per visible column, in the order the table shows them.
  const headerCells: string[] = [];
  for (const column of columns) {
    headerCells.push(escapeCsvField(column.label));
  }
  lines.push(headerCells.join(','));

  for (const row of rows) {
    const cells: string[] = [];
    for (const column of columns) {
      cells.push(escapeCsvField(column.accessor(row)));
    }
    lines.push(cells.join(','));
  }
  // UTF-8 BOM so Excel keeps `·` and `—` instead of mojibake.
  return '\uFEFF' + lines.join('\n');
}

// `execution-<owner or all>-<YYYY-MM-DD>.csv`. The owner is slugified so a name with a space
// or a comma is still a usable filename; an empty scope is `all`.
export function csvFilename(ownerName: string, day: string): string {
  const slug = slugOwner(ownerName);
  return 'execution-' + slug + '-' + day + '.csv';
}

function slugOwner(ownerName: string): string {
  const trimmed = ownerName.trim().toLowerCase();
  if (!trimmed) return 'all';
  const slug = trimmed.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return slug || 'all';
}
